import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Bot, Search, ListChecks, MessageSquare, BarChart3, Quote } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/70 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:px-10">
          <Link to="/" data-testid="landing-logo"><Logo /></Link>
          <nav className="hidden gap-8 text-sm font-medium text-muted-foreground md:flex">
            <a href="#features" className="hover:text-foreground">Features</a>
            <a href="#how" className="hover:text-foreground">How it works</a>
            <Link to="/pricing" className="hover:text-foreground" data-testid="nav-pricing">Pricing</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-login">Log in</Button></Link>
            <Link to="/signup"><Button size="sm" className="rounded-full px-5" data-testid="nav-signup">Get started</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:items-center">
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground">
                <Sparkles className="h-3 w-3 text-primary" />
                Built for India's first 10,000 customers
              </div>
              <h1 className="font-display text-5xl font-bold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
                Every meeting,<br />
                <span className="text-primary">remembered.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
                Noteology quietly joins your Zoom, Google Meet, and Teams calls — then turns the
                conversation into searchable summaries, action items, and answers you can chat with.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/signup">
                  <Button size="lg" className="rounded-full px-8 py-6 text-base" data-testid="hero-cta-primary">
                    Start for free
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="ghost" className="rounded-full px-8 py-6 text-base" data-testid="hero-cta-secondary">
                    See pricing
                  </Button>
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-6 text-xs font-mono text-muted-foreground">
                <span>NO CREDIT CARD</span>
                <span>·</span>
                <span>15 HOURS FREE</span>
                <span>·</span>
                <span>WORKS WITH GOOGLE & MS CALENDAR</span>
              </div>
            </div>
            <div className="relative">
              <div className="relative aspect-square overflow-hidden rounded-2xl border border-border bg-card shadow-[0_30px_80px_-20px_rgba(194,65,12,0.18)]">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/3bfad991-7fc9-4c85-b4b7-558ca5444229/images/055f79dad6fd5ffb9bcbe510091d1f25143d28cc700b14ce43ac17cd5fb96f42.png"
                  alt="Noteology hero"
                  className="h-full w-full object-cover"
                />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 hidden w-72 rounded-xl border border-border bg-card p-4 shadow-xl md:block">
                <div className="text-xs font-mono uppercase text-muted-foreground">Action item</div>
                <div className="mt-1 text-sm font-medium">Send Wipro buyer holding email by Wednesday</div>
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <span className="inline-block h-2 w-2 rounded-full bg-primary" />
                  <span className="font-mono text-muted-foreground">extracted by AI · 0:32 · Priya Sharma</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="border-y border-border bg-card/30 py-14">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <p className="text-center text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
            TRUSTED BY TEAMS AT INNOVATIVE COMPANIES
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-12 gap-y-6 opacity-70">
            {["razorpay", "zomato", "swiggy", "freshworks", "cred", "groww"].map((b) => (
              <span key={b} className="font-display text-2xl font-bold tracking-tighter text-muted-foreground">
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features Bento */}
      <section id="features" className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-primary">FEATURES</div>
          <h2 className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl">
            The AI notetaker that respects your attention.
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
          <div className="md:col-span-7 rounded-2xl border border-border bg-card p-8">
            <Bot className="h-6 w-6 text-primary" />
            <h3 className="mt-6 font-display text-2xl font-medium">Invisible AI, always on</h3>
            <p className="mt-3 max-w-md text-muted-foreground">
              A polite bot joins every meeting on your calendar. You focus on the conversation; Noteology
              captures the rest.
            </p>
            <div className="mt-8 overflow-hidden rounded-xl border border-border">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/3bfad991-7fc9-4c85-b4b7-558ca5444229/images/a25a572f5e1a93846c49ee7cd73f9bc0640bf9548ba08689eb75e5ff80dd0342.png"
                alt="data viz"
                className="h-56 w-full object-cover"
              />
            </div>
          </div>

          <div className="md:col-span-5 rounded-2xl border border-border bg-card p-8">
            <ListChecks className="h-6 w-6 text-primary" />
            <h3 className="mt-6 font-display text-2xl font-medium">Action items, automatically</h3>
            <p className="mt-3 text-muted-foreground">
              Every commitment gets an owner, a task, and a due date — extracted from the conversation
              by Claude.
            </p>
            <ul className="mt-6 space-y-2 font-mono text-sm text-muted-foreground">
              <li>· Owner attribution from speaker</li>
              <li>· Priority inferred from context</li>
              <li>· One-click status updates</li>
            </ul>
          </div>

          <div className="md:col-span-4 rounded-2xl border border-border bg-card p-8">
            <Search className="h-6 w-6 text-primary" />
            <h3 className="mt-6 font-display text-xl font-medium">Search every word</h3>
            <p className="mt-3 text-sm text-muted-foreground">
              Full-text search across your entire meeting history. Find the moment, not just the meeting.
            </p>
          </div>

          <div className="md:col-span-4 rounded-2xl border border-border bg-card p-8">
            <MessageSquare className="h-6 w-6 text-primary" />
            <h3 className="mt-6 font-display text-xl font-medium">Chat with the call</h3>
            <p className="mt-3 text-sm text-muted-foreground">
              Ask "What did pricing look like?" or "Draft a follow-up to Priya". The transcript is the
              context.
            </p>
          </div>

          <div className="md:col-span-4 rounded-2xl border border-border bg-card p-8">
            <BarChart3 className="h-6 w-6 text-primary" />
            <h3 className="mt-6 font-display text-xl font-medium">Talk-time insights</h3>
            <p className="mt-3 text-sm text-muted-foreground">
              Speaker breakdowns, sentiment, topic frequency. Coaching data without a coach.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-y border-border bg-card/30">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-primary">HOW IT WORKS</div>
            <h2 className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl">
              Three steps. Then nothing.
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
            {[
              { n: "01", t: "Connect your calendar", d: "Sign in with Google. Noteology reads upcoming meetings." },
              { n: "02", t: "Our bot joins", d: "Recall.ai-powered bot quietly attends Zoom, Meet, and Teams." },
              { n: "03", t: "Insights arrive", d: "Within minutes: summary, action items, and a chat interface." },
            ].map((s, i) => (
              <div key={s.n} className="relative">
                <div className="font-mono text-sm text-primary">{s.n}</div>
                <h3 className="mt-4 font-display text-2xl font-medium">{s.t}</h3>
                <p className="mt-3 text-muted-foreground">{s.d}</p>
                {i < 2 && (
                  <div className="absolute top-3 right-0 hidden h-px w-12 bg-border md:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
        <div className="rounded-2xl border border-border bg-card p-10 md:p-16">
          <Quote className="h-8 w-8 text-primary" />
          <blockquote className="mt-6 font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl">
            "Noteology gives my team back four hours every week. We stopped taking notes — we just listen
            now, and the follow-ups happen on their own."
          </blockquote>
          <div className="mt-8 flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center font-medium text-primary">PI</div>
            <div>
              <div className="font-medium">Priya Iyer</div>
              <div className="text-sm text-muted-foreground">VP Customer Success · SaaS scale-up</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <div className="rounded-2xl bg-stone-900 p-10 text-stone-50 md:p-16 dark:bg-card dark:border dark:border-border">
          <h2 className="font-display text-4xl font-bold tracking-tighter sm:text-5xl">
            Stop taking notes.
          </h2>
          <p className="mt-4 max-w-md text-stone-300">
            15 hours free, every month. No credit card. Cancel anything, anytime.
          </p>
          <div className="mt-8 flex gap-3">
            <Link to="/signup">
              <Button size="lg" className="rounded-full bg-primary px-8 py-6 text-base" data-testid="footer-cta-signup">
                Get started free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-10 md:px-10">
          <Logo />
          <div className="font-mono text-xs text-muted-foreground">© 2026 NOTEWISE · MADE IN INDIA</div>
        </div>
      </footer>
    </div>
  );
}
